import { useEffect, useMemo } from "react";
import { Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useFindTenantByIdTenantsIdGetQuery,
  useUpdateSubjectSubjectsIdPatchMutation,
} from "api";
import { RadioInput, SelectInput, TextInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { ISubject } from "interfaces/subject.interface";
import { IZone } from "interfaces/zone.interface";
import {
  toastErrorPositionCenter,
  toastSuccessPositionCenter,
} from "utils/toast.util";

import { SubjectFormSchema, subjectFormSchema } from "./subjectFormSchema";

import { FieldColumns } from "../../components/FieldColumns";
import { FormActions } from "../../components/FormActions";
import { CustomFields } from "../components/CustomFields";

type SubjectEditFormProps = {
  subject: ISubject;
  zones: IZone[];
  disabled?: boolean;
};

export function SubjectEditForm({
  subject,
  disabled,
  zones,
}: SubjectEditFormProps) {
  const { t } = useTranslation();

  const { data: tenantResponse } = useFindTenantByIdTenantsIdGetQuery({
    id: subject.tenant_id,
  });

  const [updateSubject, { isLoading }] =
    useUpdateSubjectSubjectsIdPatchMutation();

  const getCustomFieldDefaults = useMemo(() => {
    const defaults: Record<string, any> = {};

    // Defaults from tenantResponse
    tenantResponse?.data?.custom_fields?.forEach((field) => {
      if (field.id && field.default !== undefined && field.default !== null) {
        defaults[field.id] = field.default;
      }
    });

    // Override with subject values
    Object.entries(subject.custom_fields || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        defaults[key] = value;
      }
    });

    return defaults;
  }, [subject.custom_fields, tenantResponse?.data?.custom_fields]);

  const getFormDefaults = useMemo(
    () => ({
      ...subject,
      sex: subject.sex ?? undefined,
      birth_year: subject.birth_year ?? undefined,
      custom_fields: getCustomFieldDefaults,
    }),
    [subject, getCustomFieldDefaults],
  );

  const { handleSubmit, control, setValue, reset } = useForm<SubjectFormSchema>(
    {
      resolver: zodResolver(subjectFormSchema),
      defaultValues: getFormDefaults,
    },
  );

  useEffect(() => {
    const newDefaults = {
      ...subject,
      sex: subject.sex ?? undefined,
      birth_year: subject.birth_year ?? undefined,
      custom_fields: getCustomFieldDefaults,
    };
    reset(newDefaults);
  }, [subject, getCustomFieldDefaults, reset]);

  const onSubmit = async (formData: SubjectFormSchema) => {
    const customFields = { ...formData.custom_fields };

    Object.keys(customFields).forEach((key) => {
      const value = customFields[key];
      // Only remove if the value is undefined, null, or empty string
      // Keep "0" and other falsy but valid values like false, 0, etc.
      if (value === undefined || value === null || value === "") {
        delete customFields[key];
      }
    });

    try {
      await updateSubject({
        id: subject.id,
        subjectUpdate: {
          zone_id: formData.zone_id,
          identifier: formData.identifier,
          name: formData.name,
          sex: formData.sex,
          birth_year: formData.birth_year,
          custom_fields: customFields as { [key: string]: string },
        },
      });

      toastSuccessPositionCenter(t("subject_updated_successfully"));
    } catch (error) {
      console.error("Failed to update subject:", error);
      toastErrorPositionCenter(t("Try again"));
    }
  };

  const customFields =
    tenantResponse?.data?.custom_fields?.filter(
      (field) => field.entity === "subject" && field.id,
    ) || [];

  return (
    <form onSubmit={handleSubmit(onSubmit, console.error)}>
      <Stack gap={2}>
        <SectionCard title={t("Subject Information")}>
          <FieldColumns>
            <SelectInput
              control={control}
              fieldName={"zone_id"}
              label={t("Zone")}
              disabled={disabled}
              options={zones.map((zone) => ({
                ...zone,
                label: zone.name,
                disabled: !zone.hasAccess,
                padding: (zone.level - 1) * 2,
              }))}
              placeholder={t("Select a zone")}
              onChange={(event) => {
                // Ensure the field is immediately marked as touched
                setValue("zone_id", event.target.value, { shouldTouch: true });
              }}
            />
            <TextInput
              fieldName={"identifier"}
              control={control}
              label={t("Subject ID")}
              disabled={disabled}
              variant={"outlined"}
              required
            />
            <TextInput
              fieldName={"name"}
              control={control}
              label={t("Description (optional)")}
              disabled={disabled}
              variant={"outlined"}
            />
            <RadioInput
              fieldName={"sex"}
              control={control}
              label={t("Sex")}
              direction={"row"}
              options={[
                { id: "male", label: t("Male") },
                { id: "female", label: t("Female") },
              ]}
              disabled={disabled}
            />
            <TextInput
              fieldName={"birth_year"}
              type={"number"}
              control={control}
              label={t("Birth Year")}
              disabled={disabled}
              required
              min={1}
            />
          </FieldColumns>
        </SectionCard>
        <CustomFields
          customFields={customFields}
          control={control}
          disabled={disabled}
        />
        <FormActions
          submitText={t("Save")}
          backText={t("Cancel")}
          backHref={"../subjects"}
          isLoading={isLoading}
        />
      </Stack>
    </form>
  );
}
